/**
 * Command System Permissions
 *
 * Defines all permission constants used throughout the command system.
 * These permissions should match the backend permission definitions.
 *
 * @module constants/Permissions
 */

/**
 * Command System Permission Constants
 *
 * Permission hierarchy:
 * - VIEW_COMMANDS: Basic permission to see commands
 * - EXECUTE_COMMANDS: Permission to execute any low/medium risk commands
 * - EXECUTE_HIGH_RISK_COMMANDS: Additional permission for high-risk commands
 * - EXECUTE_CRITICAL_COMMANDS: Additional permission for critical commands
 * - MANAGE_COMMANDS: Full administrative access to command management
 */
export const COMMAND_PERMISSIONS = {
  // Command Management (Dev Menu)
  /** View commands in the command management resource */
  MANAGE_COMMANDS: 'manage_commands',
  /** Create new commands in the system */
  CREATE_COMMANDS: 'create_commands',
  /** Edit existing commands */
  EDIT_COMMANDS: 'edit_commands',
  /** Delete commands from the system */
  DELETE_COMMANDS: 'delete_commands',
  /** View commands list in admin interface */
  VIEW_COMMANDS: 'view_commands',

  // Command Execution (Unit Detail)
  /** Execute commands on units (low and medium risk) */
  EXECUTE_COMMANDS: 'execute_commands',
  /** View execution history for units */
  VIEW_EXECUTIONS: 'view_command_executions',
  /** View detailed execution information */
  VIEW_EXECUTION_DETAILS: 'view_execution_details',

  // High-Risk Commands
  /** Execute commands marked as critical (requires EXECUTE_COMMANDS as well) */
  EXECUTE_CRITICAL_COMMANDS: 'execute_critical_commands',
  /** Execute commands marked as high-risk (requires EXECUTE_COMMANDS as well) */
  EXECUTE_HIGH_RISK_COMMANDS: 'execute_high_risk_commands',
} as const;

/**
 * Type-safe permission string
 */
export type CommandPermission = (typeof COMMAND_PERMISSIONS)[keyof typeof COMMAND_PERMISSIONS];

/**
 * Helper function to check if user has a specific permission
 *
 * @param permissions - Array of permission strings from usePermissions hook
 * @param requiredPermission - The permission to check for
 * @returns boolean indicating if permission exists
 *
 * @example
 * ```typescript
 * const { permissions } = usePermissions();
 * const canExecute = hasPermission(permissions, COMMAND_PERMISSIONS.EXECUTE_COMMANDS);
 * ```
 */
export const hasPermission = (permissions: string[] | undefined, requiredPermission: CommandPermission): boolean => {
  return permissions?.includes(requiredPermission) ?? false;
};

/**
 * Helper function to check if user has multiple permissions
 *
 * @param permissions - Array of permission strings from usePermissions hook
 * @param requiredPermissions - Array of permissions to check for
 * @param requireAll - If true, user must have all permissions. If false, any permission is sufficient.
 * @returns boolean indicating if user has required permissions
 *
 * @example
 * ```typescript
 * const { permissions } = usePermissions();
 * // Check if user has EITHER view OR execute permission
 * const canAccess = hasPermissions(permissions, [
 *   COMMAND_PERMISSIONS.VIEW_COMMANDS,
 *   COMMAND_PERMISSIONS.EXECUTE_COMMANDS
 * ], false);
 * ```
 */
export const hasPermissions = (
  permissions: string[] | undefined,
  requiredPermissions: CommandPermission[],
  requireAll: boolean = true,
): boolean => {
  if (!permissions) return false;

  if (requireAll) {
    return requiredPermissions.every((perm) => permissions.includes(perm));
  } else {
    return requiredPermissions.some((perm) => permissions.includes(perm));
  }
};

/**
 * Helper function to check if user can execute a command based on danger level
 *
 * @param permissions - Array of permission strings from usePermissions hook
 * @param dangerLevel - The danger level of the command
 * @returns boolean indicating if user can execute this danger level
 *
 * @example
 * ```typescript
 * const { permissions } = usePermissions();
 * const canExecuteCritical = canExecuteDangerLevel(permissions, 'critical');
 * ```
 */
export const canExecuteDangerLevel = (
  permissions: string[] | undefined,
  dangerLevel: 'low' | 'medium' | 'high' | 'critical',
): boolean => {
  if (!permissions) return false;

  // Must have basic execute permission
  if (!permissions.includes(COMMAND_PERMISSIONS.EXECUTE_COMMANDS)) {
    return false;
  }

  // Check specific danger level permissions
  switch (dangerLevel) {
    case 'critical':
      return permissions.includes(COMMAND_PERMISSIONS.EXECUTE_CRITICAL_COMMANDS);
    case 'high':
      return permissions.includes(COMMAND_PERMISSIONS.EXECUTE_HIGH_RISK_COMMANDS);
    case 'medium':
    case 'low':
      return true; // Basic execute permission is sufficient
    default:
      return false;
  }
};
